import type { EmbedHtmlModel, EmbedHtmlStyles } from '@blocksuite/affine-model';
import { BlockSelection } from '@blocksuite/std';
import { html } from 'lit';
import { query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { type StyleInfo, styleMap } from 'lit/directives/style-map.js';

import { EmbedBlockComponent } from '../common/embed-block-element.js';
import { renderHtmlEmbed, renderHtmlEmbedOverlay } from './html-embed.js';
import { HtmlIcon, styles } from './styles.js';

function isFullHtmlDocument(html: string): boolean {
  return /^\s*(?:<!doctype\s+html[^>]*>\s*)?<html[\s>]/iu.test(html);
}

export class EmbedHtmlBlockComponent extends EmbedBlockComponent<EmbedHtmlModel> {
  static override styles = styles;

  override _cardStyle: (typeof EmbedHtmlStyles)[number] = 'html';

  close = () => {
    document.exitFullscreen().catch(console.error);
  };

  protected embedHtmlStyle: StyleInfo = {};

  open = () => {
    this.iframeWrapper?.requestFullscreen().catch(console.error);
  };

  refreshData = () => {};

  private _handleDoubleClick(event: MouseEvent) {
    event.stopPropagation();
    this.open();
  }

  private _selectBlock() {
    const selectionManager = this.host.selection;
    const blockSelection = selectionManager.create(BlockSelection, {
      blockId: this.blockId,
    });
    selectionManager.setGroup('note', [blockSelection]);
  }

  protected _handleClick(event: MouseEvent) {
    event.stopPropagation();
    this._selectBlock();
  }

  override connectedCallback() {
    super.connectedCallback();
    this._cardStyle = this.model.props.style;

    this.disposables.add(
      this.model.propsUpdated.subscribe(({ key }) => {
        if (key === 'html') this._externalLoaded = false;
      })
    );
  }

  override renderBlock(): unknown {
    const titleText = 'Basic HTML Page Structure';
    const rawHtml = this.model.props.html ?? '';

    const htmlSrc = isFullHtmlDocument(rawHtml)
      ? rawHtml
      : `
      <style>
        body {
          margin: 0;
        }
      </style>
      ${rawHtml}
    `;

    return this.renderEmbed(() => {
      if (!rawHtml) {
        return html` <div class="affine-html-empty">Empty</div>`;
      }
      return html`
        <div
          class=${classMap({
            'affine-embed-html-block': true,
            selected: this.selected$.value,
          })}
          style=${styleMap(this.embedHtmlStyle)}
          @click=${this._handleClick}
          @dblclick=${this._handleDoubleClick}
        >
          <div class="affine-embed-html">
            <div class="affine-embed-html-iframe-container">
              <div class="embed-html-block-iframe-wrapper" allowfullscreen>
                ${renderHtmlEmbed({
                  htmlSrc,
                  loaded: this._externalLoaded,
                  onLoad: this._loadExternal,
                })}
                <embed-html-fullscreen-toolbar
                  .embedHtml=${this}
                ></embed-html-fullscreen-toolbar>
              </div>

              ${renderHtmlEmbedOverlay({
                loaded: this._externalLoaded,
                overlayHidden: !this.showOverlay$.value,
              })}
            </div>
          </div>

          <div class="affine-embed-html-title">
            <div class="affine-embed-html-title-icon">${HtmlIcon}</div>

            <div class="affine-embed-html-title-text">${titleText}</div>
          </div>
        </div>
      `;
    });
  }

  @query('.embed-html-block-iframe-wrapper')
  accessor iframeWrapper!: HTMLDivElement;

  /** Released by the reader for this view only; never stored. */
  @state()
  private accessor _externalLoaded = false;

  private readonly _loadExternal = () => {
    this._externalLoaded = true;
  };
}
